import { useCallback } from 'react';
import { Entity } from '@backstage/catalog-model';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import LaunchIcon from '@material-ui/icons/Launch';
import { StatusError, StatusOK } from '@backstage/core-components';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { usePolled } from './usePolled';

/** Verde água SGA. */
const SGA_COLOR = '#00e5ff';
const ERROR_COLOR = '#f44336';

const POLL_INTERVAL_MS = 15_000;

const DEFAULT_APP_URL_TEMPLATE = 'https://{component}-hml.azurewebsites.net';
const DEFAULT_RESOURCE_GROUP_TEMPLATE = '{component}-hml-rg';
const DEFAULT_APP_INSIGHTS_TEMPLATE = '{component}-hml-ai';

const applyTemplate = (template: string, component: string) =>
  template.replace('{component}', component);

/**
 * Lê uma string de config tratando valor vazio como ausente. Uma variável de
 * ambiente sem valor chega como string vazia até aqui, e `getOptionalString`
 * lança nesse caso — o que quebraria o card inteiro por causa de config.
 */
const readOptionalString = (
  config: { getOptional(key: string): unknown },
  key: string,
): string | undefined => {
  const value = config.getOptional(key);
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

/** Exportado para os testes; a montagem real é feita pelo blueprint abaixo. */
export const AzureHealthCardContent = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const component = entity.metadata.name;

  const fetcher = useCallback(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('azure-health');
    const response = await fetchApi.fetch(
      `${baseUrl}/health/${encodeURIComponent(component)}`,
    );

    // O backend repassa o status do alvo, então 2xx é o único caso saudável.
    // Um 401/403 aqui seria do próprio Backstage, e cai no mesmo balde.
    return { online: response.ok, checkedAt: new Date().toLocaleTimeString() };
  }, [discoveryApi, fetchApi, component]);

  const { data, loading } = usePolled(fetcher, POLL_INTERVAL_MS);

  if (loading && !data) {
    return <LinearProgress />;
  }

  const isOnline = data?.online ?? false;
  const accent = isOnline ? SGA_COLOR : ERROR_COLOR;

  const appUrl = applyTemplate(
    readOptionalString(configApi, 'azureMonitoring.appUrlTemplate') ??
      DEFAULT_APP_URL_TEMPLATE,
    component,
  );

  // Sem subscription configurada não há link de Insights para montar.
  const subscriptionId = readOptionalString(
    configApi,
    'azureMonitoring.subscriptionId',
  );
  const resourceGroup = applyTemplate(
    readOptionalString(configApi, 'azureMonitoring.resourceGroupTemplate') ??
      DEFAULT_RESOURCE_GROUP_TEMPLATE,
    component,
  );
  const appInsightsName = applyTemplate(
    readOptionalString(configApi, 'azureMonitoring.appInsightsTemplate') ??
      DEFAULT_APP_INSIGHTS_TEMPLATE,
    component,
  );
  const insightsUrl = subscriptionId
    ? `https://portal.azure.com/#@/resource/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/microsoft.insights/components/${appInsightsName}/overview`
    : undefined;

  return (
    <Card
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `6px solid ${accent}`,
        background: isOnline
          ? 'rgba(0, 229, 255, 0.02)'
          : 'rgba(244, 67, 54, 0.02)',
        transition: 'all 0.5s ease',
      }}
    >
      <CardContent>
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: 'bold',
            marginBottom: 12,
            textTransform: 'uppercase',
            color: '#777',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <AssessmentIcon
            style={{ fontSize: '1.2rem', marginRight: 8, color: accent }}
          />
          Observabilidade
        </Typography>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          style={{
            background: isOnline
              ? 'rgba(0, 229, 255, 0.1)'
              : 'rgba(244, 67, 54, 0.1)',
            borderRadius: 8,
            border: `1px solid ${accent}`,
            marginBottom: 16,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              style={{
                fontWeight: 'bold',
                color: isOnline ? '#008b9a' : ERROR_COLOR,
                lineHeight: 1.2,
              }}
            >
              {isOnline ? 'SISTEMA ONLINE' : 'SISTEMA OFFLINE'}
            </Typography>
            <Typography variant="caption" style={{ color: '#666' }}>
              Verificado: {data?.checkedAt ?? '-'}
            </Typography>
          </Box>
          {isOnline ? <StatusOK /> : <StatusError />}
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={insightsUrl ? 6 : 12}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<LaunchIcon />}
              href={appUrl}
              target="_blank"
              style={{ fontSize: '0.65rem' }}
            >
              App
            </Button>
          </Grid>
          {insightsUrl && (
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<AssessmentIcon />}
                href={insightsUrl}
                target="_blank"
                style={{ fontSize: '0.65rem' }}
              >
                Insights
              </Button>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

/**
 * Todo Component, independente do `spec.type` — é a mesma abrangência que o
 * card tinha no projeto antigo, onde o overview caía no mesmo layout para
 * `service`, `website` e o fallback.
 */
export const isHealthCardApplicable = (entity: Entity) =>
  entity.kind === 'Component';

export const AzureHealthCard = EntityCardBlueprint.make({
  name: 'azure-health',
  params: {
    // `info` coloca o card na coluna estreita e fixa do topo, junto do About —
    // sem isso ele cai no fim da coluna larga de conteúdo.
    type: 'info',
    filter: isHealthCardApplicable,
    loader: async () => <AzureHealthCardContent />,
  },
});
